/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import {
  editChangeOfVenue,
  fetchChangeOfVenueById,
} from "services/caseServices";
import { ChangeOfVenue, ChangeOfVenueStatus, Roles } from "types";
import { errorHandlerOnForm, hasRole } from "utils/helper";
import ChangeOfVenueFeedback from "./fragment/ChangeOfVenueFeedback";

type Props = {
  selectedId: string | number;
};

const ChangeOfVenueDetail: React.FC<Props> = ({ selectedId }) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const [venue, setVenue] = useState<ChangeOfVenue>();
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);

  const fetchData = () => {
    if (!selectedId) return;
    fetchChangeOfVenueById(selectedId)
      .then((data: ChangeOfVenue) => {
        setVenue(data);
      })
      .catch((error) => {
        showTooltip(
          errorHandlerOnForm(error) || "Error fetching case details",
          "error"
        );
      });
  };

  useEffect(() => {
    fetchData();
  }, [selectedId, showTooltip]);

  const updateStatus = (status: string) => {
    if (!selectedId) return;
    editChangeOfVenue({
      id: selectedId,
      status: status,
    })
      .then(() => {
        showTooltip("Successfully Updated!", "success");
        fetchData();
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error"
        );
      });
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            <span className="text-2xl">🏛️</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Change of Venue Details
            </h1>
            <p className="text-gray-600 mt-1">
              Review and manage venue change requests and decisions.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {!venue ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">
                Loading venue details...
              </span>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              {/* Action Buttons for Coordinator */}
              {isCoordinator && venue.status === "pending" && (
                <div className="flex flex-wrap gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
                  <button
                    className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-colors"
                    onClick={() => {
                      updateStatus("approved");
                    }}
                  >
                    Approve
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-white text-gray-700 font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
                    onClick={() => {
                      updateStatus("respond_back_to_prosecutor");
                    }}
                  >
                    Respond Back to Prosecutor
                  </button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600">
                    Reason Summary
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      {venue.reason_summary || "N/A"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600">Date</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {venue.date
                      ? new Date(venue.date).toLocaleDateString()
                      : "N/A"}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600">
                    Prosecution Case Number
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {venue.prosecution_case_number || "N/A"}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600">
                    Court Case Number
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {venue.court_case_number || "N/A"}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600">
                    Registration Number
                  </p>
                  <p className="text-sm font-semibold text-gray-900">
                    {venue.registration_number || "N/A"}
                  </p>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-600">Status</p>
                  <span
                    className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getVenueStatusColor(
                      venue.status
                    )}`}
                  >
                    {venue.status && ChangeOfVenueStatus[venue.status]}
                  </span>
                </div>
                <div className="md:col-span-2 space-y-2">
                  <p className="text-sm font-medium text-gray-600">
                    Summary File
                  </p>
                  {venue.summary_file_info?.file_url ? (
                    <a
                      href={venue.summary_file_info?.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                    >
                      📄 View Summary File
                    </a>
                  ) : (
                    <p className="text-sm text-gray-500">No file available</p>
                  )}
                </div>
              </div>

              {/* File History */}
              {venue.file_history && venue.file_history.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    File History
                  </h3>
                  <div className="space-y-3">
                    {venue.file_history.map((fh) => (
                      <div
                        key={fh.id}
                        className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3 mb-3 md:mb-0">
                          <a
                            href={fh.uploaded_file}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View Uploaded File
                          </a>
                        </div>
                        <div className="flex flex-col md:items-end">
                          <p className="text-sm text-gray-600">
                            Uploaded By: {fh.uploaded_by || "Unknown"}
                          </p>
                          <p className="text-sm text-gray-600">
                            Date:{" "}
                            {fh.uploaded_at
                              ? new Date(fh.uploaded_at).toLocaleString()
                              : "-"}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {venue && (
            <ChangeOfVenueFeedback
              venue={venue}
              venue_feedbacks={venue?.feedbacks || []}
              setRefetch={() => fetchData()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function for venue status colors
const getVenueStatusColor = (status?: string) => {
  if (!status) return "bg-gray-100 text-gray-800";

  switch (status.toLowerCase()) {
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "respond_back_to_prosecutor":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default ChangeOfVenueDetail;
